import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const buildPaymentUrl = ({
  username,
  amount,
  note,
  provider,
}: {
  username: string;
  amount: string;
  note: string;
  provider: string;
}): string => {
  if (provider === "venmo") {
    // Replace regular spaces with non-breaking spaces to prevent Venmo from showing + symbols
    const venmoNote = note || "dollarback.cash 💸";
    const encodedNote = encodeURIComponent(venmoNote.replace(/ /g, "\u00A0"));
    return `https://venmo.com/${username}?txn=pay&amount=${amount}&note=${encodedNote}`;
  }

  if (provider === "paypal") {
    return `https://paypal.me/${username}/${amount}`;
  }

  if (provider === "cashapp") {
    return `https://cash.app/$${username}/${amount}`;
  }

  return "";
};

export default function PayPage() {
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name")?.trim() ?? null;
  const username = searchParams.get("username")?.trim() ?? null;
  const amount = searchParams.get("amount")?.trim() ?? null;
  const note = searchParams.get("note")?.trim() ?? null;
  const provider = searchParams.get("provider")?.trim() ?? null;

  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!username || !amount || !note || !provider) {
      return;
    }

    setPaymentUrl(
      buildPaymentUrl({
        username,
        amount,
        note,
        provider,
      })
    );
  }, [username, amount, note, provider]);

  useEffect(() => {
    if (!paymentUrl) {
      return;
    }

    const timer = setTimeout(() => {
      window.location.href = paymentUrl;
    }, 0);

    return () => clearTimeout(timer);
  }, [paymentUrl]);

  const formattedAmount = amount
    ? `$${parseFloat(amount).toFixed(2)}`
    : "{$0.00}";

  // Helper to get provider logo
  const getProviderLogo = (providerName: string | null) => {
    if (!providerName) return null;

    // Convert provider to lowercase for comparison
    const provider = providerName.toLowerCase();

    if (provider === "venmo") {
      return (
        <img
          src="/venmo.png"
          alt="Venmo"
          width={20}
          height={20}
          className="inline-block mx-1 rounded"
        />
      );
    }

    if (provider === "paypal") {
      return (
        <img
          src="/paypal.png"
          alt="PayPal"
          width={20}
          height={20}
          className="inline-block mx-1 rounded"
        />
      );
    }

    if (provider === "cashapp") {
      return (
        <img
          src="/cashapp.png"
          alt="Cash App"
          width={20}
          height={20}
          className="inline-block mx-1 rounded"
        />
      );
    }

    return null;
  };

  // Capitalize provider name
  const capitalizeProvider = (providerName: string | null) => {
    if (!providerName) return "";

    if (providerName.toLowerCase() === "venmo") return "Venmo";
    if (providerName.toLowerCase() === "paypal") return "PayPal";
    if (providerName.toLowerCase() === "cashapp") return "Cash App";

    return providerName.charAt(0).toUpperCase() + providerName.slice(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-600 via-indigo-500 to-blue-400 flex items-start justify-center pt-16 p-4">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden w-full max-w-md relative">
        {/* Dollarback logo in top right */}
        <div className="absolute top-4 right-4">
          <a href="/">
            <img
              src="/logo.png"
              alt="DollarBack"
              width={60}
              height={60}
              className="rounded-full"
            />
          </a>
        </div>

        {/* Payment details card */}
        <div className="p-6">
          {/* Payment information */}
          {paymentUrl && (
            <div className="mb-5">
              <h2 className="font-medium text-gray-500 mb-2">
                Redirecting to pay:
              </h2>

              {/* Line 1: Recipient and provider */}
              <div className="mb-2">
                <span className="text-lg font-bold text-gray-900">
                  {name || username}
                </span>
                <span className="text-sm font-normal text-gray-500 inline-flex items-center ml-2">
                  on {getProviderLogo(provider)} {capitalizeProvider(provider)}
                </span>
              </div>

              {/* Line 2: Amount and note - vertical stack for better wrapping */}
              <div className="mb-6">
                <p className="text-sm text-gray-600 mt-1">
                  <span className="text-2xl font-bold text-gray-900">
                    {formattedAmount}
                  </span>
                  {note && <span className="ml-2">{`for "${note}"`}</span>}
                </p>
              </div>

              <a
                href={paymentUrl}
                className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium text-center hover:bg-indigo-700 flex items-center justify-center"
              >
                Complete Payment
                <ArrowRight className="ml-1 h-4 w-4" />
              </a>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-gray-200 my-4"></div>

          {/* What is DollarBack? */}
          <div>
            <div className="flex items-center mb-3">
              <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-lg mr-2">
                💸
              </div>
              <div>
                <h1 className="font-bold text-gray-900">What is DollarBack?</h1>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              DollarBack creates payment links that work across Venmo, PayPal,
              and Cash App — no signup needed.
            </p>

            {/* Key benefits */}
            <div className="flex justify-between mb-4 px-6">
              <div className="text-center w-16">
                <div className="text-indigo-500 text-lg">⚡</div>
                <p className="text-xs text-gray-600">Fast</p>
              </div>
              <div className="text-center w-16">
                <div className="text-indigo-500 text-lg">💳</div>
                <p className="text-xs text-gray-600">Universal</p>
              </div>
              <div className="text-center w-16">
                <div className="text-indigo-500 text-lg">🔒</div>
                <p className="text-xs text-gray-600">Secure</p>
              </div>
            </div>

            <a
              href="/"
              className="w-full py-2 bg-gray-100 text-gray-800 rounded-lg font-medium text-center hover:bg-gray-200 block"
            >
              Create Your Own Link
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
